/**
 * Config Hot Reload Utilities for Config Editor
 * 
 * Enables live preview updates without page reload
 */

import React from 'react';

/**
 * Send config update to target window (iframe, popup, etc.)
 */
function sendConfigUpdate(
    targetWindow: Window,
    config: Record<string, any>,
    targetOrigin = '*'
) {
    const message = {
        type: 'TAGADAPAY_CONFIG_UPDATE',
        config,
        timestamp: Date.now(),
    };

    targetWindow.postMessage(message, targetOrigin);
}

/**
 * Send config update to all iframes on the page
 */
function broadcastConfigUpdate(
    config: Record<string, any>,
    selector = 'iframe[data-preview-frame]'
) {
    if (typeof document === 'undefined') return;

    const iframes = document.querySelectorAll<HTMLIFrameElement>(selector);

    iframes.forEach((iframe) => {
        if (iframe.contentWindow) {
            sendConfigUpdate(iframe.contentWindow, config);
        }
    });

    if (iframes.length > 0) {
        console.log(`[ConfigEditor] Config update pushed to ${iframes.length} preview iframe(s)`);
    }
}

/**
 * Debounce helper for config updates
 */
function debounceConfigUpdate(
    fn: (config: Record<string, any>) => void,
    delay = 150
): (config: Record<string, any>) => void {
    let timeoutId: NodeJS.Timeout | null = null;

    return (config: Record<string, any>) => {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }

        timeoutId = setTimeout(() => {
            fn(config);
            timeoutId = null;
        }, delay);
    };
}

/**
 * Send config update to preview iframe(s)
 * Automatically debounced to prevent too many updates
 */
export const pushConfigUpdate = debounceConfigUpdate((config: Record<string, any>) => {
    // Broadcast to all iframes in preview containers
    broadcastConfigUpdate(config, 'iframe[data-preview-frame]');
}, 150); // 150ms debounce

/**
 * Send immediate (non-debounced) config update
 * Use this for critical updates that need to happen immediately
 */
export function pushConfigUpdateImmediate(config: Record<string, any>) {
    broadcastConfigUpdate(config, 'iframe[data-preview-frame]');
}

/**
 * React hook for config hot reload
 * Call this whenever config changes to push updates to preview
 */
export function useConfigHotReload(config: Record<string, any> | null, enabled = true) {
    React.useEffect(() => {
        if (!enabled || !config) return;

        pushConfigUpdate(config);
    }, [config, enabled]);
}

